import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Brain, Heart, Lightbulb, CheckCircle2, X } from "lucide-react";

interface SkillDetailDialogProps {
  skillId: string | null;
  onClose: () => void;
}

interface SkillContent {
  name: string;
  difficulty: string;
  time: string;
  whatItIs: string;
  whyItWorks: string;
  theScience: string;
  howToPractice: string[];
  tips?: string[];
}

const skillsContent: Record<string, SkillContent> = {
  // DISTRESS TOLERANCE SKILLS
  'tipp': {
    name: 'TIPP',
    difficulty: 'Easy',
    time: '5-10 min',
    whatItIs: 'TIPP is a rapid crisis intervention technique that uses physical changes to quickly reduce intense emotional arousal. It stands for Temperature, Intense exercise, Paced breathing, and Progressive relaxation.',
    whyItWorks: 'When you\'re in crisis, your nervous system is in overdrive. TIPP techniques work because they directly target your body\'s physiology, creating immediate changes that signal safety to your brain. You can\'t think your way out of a panic attack, but you can change your body to change your mind.',
    theScience: 'These techniques activate the parasympathetic nervous system (your "rest and digest" system) and override the sympathetic nervous system (your "fight or flight" response). Cold temperature triggers the dive reflex, slowing heart rate. Intense exercise burns off stress hormones like cortisol and adrenaline. Paced breathing increases CO2 levels, signalling your brain that you\'re safe.',
    howToPractice: [
      'Temperature: Hold ice cubes, splash cold water on your face, or take a cold shower. The cold triggers an automatic calming response.',
      'Intense exercise: Do jumping jacks, run in place, or do push-ups for 5-10 minutes. This burns off the stress chemicals flooding your system.',
      'Paced breathing: Breathe in for 4 counts, hold for 7, out for 8. Make your exhale longer than your inhale to activate calm.',
      'Progressive relaxation: Tense each muscle group for 5 seconds, then release. Start with your toes and work up to your face.'
    ],
    tips: [
      'Keep ice packs in your freezer for emergencies',
      'The colder the water, the stronger the calming effect',
      'Even 60 seconds of intense movement can make a difference',
      'Focus on the physical sensations - this grounds you in the present'
    ]
  },
  'accepts': {
    name: 'ACCEPTS',
    difficulty: 'Easy',
    time: '10-20 min',
    whatItIs: 'ACCEPTS is a distraction toolkit for surviving crisis moments without making things worse. Each letter represents a different way to redirect your mind away from overwhelming distress: Activities, Contributing, Comparisons, Emotions, Push away, Thoughts, Sensations.',
    whyItWorks: 'Sometimes the most helpful thing you can do is NOT engage with the crisis. Distraction gives your emotional brain time to settle whilst your thinking brain comes back online. It\'s not avoidance - it\'s strategic withdrawal to regroup.',
    theScience: 'Distraction works by redirecting neural activity away from the amygdala (fear centre) to other brain regions. When you engage in activities, help others, or focus on sensations, you\'re literally changing which neurons are firing. This gives your prefrontal cortex (rational brain) time to regain control.',
    howToPractice: [
      'Activities: Engage fully in something absorbing - puzzles, cleaning, art, exercise. The key is full attention.',
      'Contributing: Help someone else. Make tea for a friend, send a supportive text, volunteer. Shifting focus outward interrupts the crisis.',
      'Comparisons: Think of times you felt worse and survived. Remember that feelings pass. Compare to people in harder situations (with compassion).',
      'Emotions: Generate opposite emotions - watch comedy for sadness, calm music for anger, adventure films for boredom.',
      'Push away: Mentally put the problem in a box to deal with later. Build an imaginary wall. Schedule "worry time" for tomorrow.',
      'Thoughts: Count backwards from 100 by 7s, recite lyrics, describe objects around you in detail. Occupy your mind completely.',
      'Sensations: Hold ice, listen to loud music, take a hot shower, smell strong scents. Strong physical sensations anchor you.'
    ],
    tips: [
      'Create an "ACCEPTS emergency kit" with items for each strategy',
      'Don\'t judge yourself for needing to distract - it\'s a survival skill',
      'Combine techniques for stronger effect',
      'The goal is not to solve the problem but to survive the moment'
    ]
  },
  'improve': {
    name: 'IMPROVE the Moment',
    difficulty: 'Medium',
    time: '5-15 min',
    whatItIs: 'IMPROVE is a set of strategies to make a painful moment more bearable by changing your internal experience. It stands for Imagery, Meaning, Prayer, Relaxation, One thing in the moment, Vacation, and Encouragement.',
    whyItWorks: 'You can\'t always change your circumstances immediately, but you can change how you experience them. IMPROVE techniques create psychological distance from distress and activate internal resources for coping. They help you find meaning and hope even in difficult moments.',
    theScience: 'Visualisation and meaning-making activate the prefrontal cortex, which regulates emotion. Prayer and encouragement tap into self-compassion pathways. Taking mental "vacations" engages the same neural networks as actual pleasant experiences. The brain doesn\'t always distinguish between imagined and real experiences.',
    howToPractice: [
      'Imagery: Visualise a safe place in vivid detail. Imagine the crisis working out well. Picture yourself coping effectively.',
      'Meaning: Find purpose in the pain. What can you learn? How might this help you help others? Connect to your values.',
      'Prayer: Connect to something larger than yourself - whether that\'s God, the universe, nature, or humanity. Ask for strength.',
      'Relaxation: Use progressive muscle relaxation, gentle stretching, or calming music. Even 2 minutes helps.',
      'One thing: Focus completely on whatever you\'re doing right now. Eat one crisp slowly. Feel the water in the shower. Be fully present.',
      'Vacation: Take a mental break. Imagine you\'re on holiday. Remember a happy memory in detail. Read an absorbing book.',
      'Encouragement: Speak to yourself like you would a dear friend. "You\'ve survived before. This will pass. You\'re doing the best you can."'
    ],
    tips: [
      'Practise imagery when calm so it\'s stronger in crisis',
      'Create a playlist of encouraging songs or recordings',
      'Write encouraging statements on cards to read when struggling',
      'Remember: making the moment even slightly better is success'
    ]
  },
  'radical-acceptance': {
    name: 'Radical Acceptance',
    difficulty: 'Hard',
    time: 'Ongoing',
    whatItIs: 'Radical acceptance means completely accepting reality as it is in this moment, without fighting it or judging it. It\'s acknowledging what you cannot change, even when it\'s painful, and choosing to stop struggling against facts.',
    whyItWorks: 'Suffering = Pain × Resistance. When you fight reality ("This shouldn\'t be happening!"), you add a layer of suffering on top of the pain. Acceptance doesn\'t mean approval or giving up - it means acknowledging what is so you can respond effectively instead of just reacting emotionally.',
    theScience: 'The brain\'s default is to resist threats and discomfort. But this resistance activates chronic stress responses. Acceptance practices decrease activity in the amygdala and increase prefrontal cortex engagement. Neuroimaging shows that acceptance reduces the brain\'s pain response and improves emotional regulation.',
    howToPractice: [
      'Notice when you\'re fighting reality. Catch thoughts like "This isn\'t fair!" "Why me?" "This shouldn\'t be happening!"',
      'Turn your mind toward acceptance, again and again. Your mind will resist - that\'s normal. Gently redirect it each time.',
      'Use willing hands: Uncurl your fists, turn your palms up. This physical gesture signals acceptance to your brain.',
      'Say it out loud: "I accept that [reality]. I don\'t like it, but I accept that it\'s true right now."',
      'Practice half-smiling: A slight, gentle expression that signals non-resistance to your nervous system.',
      'Accept with your body: Relax your muscles. Drop your shoulders. Let your jaw soften. Physical tension is resistance.',
      'Remember: Acceptance is a choice you make again and again, not a one-time decision.'
    ],
    tips: [
      'Start with small acceptances before tackling big ones',
      'Acceptance doesn\'t mean you won\'t work to change things',
      'You can accept reality AND take action to improve it',
      'This is often the hardest skill - be patient with yourself'
    ]
  },

  // EMOTION REGULATION SKILLS
  'check-facts': {
    name: 'Check the Facts',
    difficulty: 'Easy',
    time: '5-10 min',
    whatItIs: 'Check the Facts is a technique to examine whether your emotional response matches the actual situation. Often our emotions are based on interpretations, assumptions, or past experiences rather than current reality.',
    whyItWorks: 'Emotions are messengers with important information, but they\'re not always accurate. When facts don\'t match feelings, checking the evidence helps you respond to what\'s actually happening rather than what your anxiety, past trauma, or catastrophic thinking tells you is happening.',
    theScience: 'The amygdala (emotion centre) reacts faster than the prefrontal cortex (thinking brain). This means you feel before you think. Checking facts engages your prefrontal cortex, which can then regulate the amygdala. Cognitive reappraisal (re-evaluating situations) is one of the most effective emotion regulation strategies according to neuroscience.',
    howToPractice: [
      'Name the emotion you\'re feeling and how intense it is (0-10).',
      'Describe the triggering event with just facts - no interpretations. What would a video camera show?',
      'Check your interpretations: What assumptions am I making? What meanings am I adding? Are these facts or thoughts?',
      'Ask: What\'s the evidence for and against my interpretation? Am I thinking in extremes? What would I tell a friend?',
      'Determine if the emotion fits the facts. If not, what emotion WOULD fit the facts?',
      'If emotion doesn\'t fit, practice opposite action (see that skill). If it does fit, problem-solve or use distress tolerance.'
    ],
    tips: [
      'Write it down - seeing it on paper helps you spot assumptions',
      'Look for words like "always," "never," "everyone" - these are usually exaggerations',
      'Remember past times your catastrophic predictions didn\'t come true',
      'Ask yourself: "Will this matter in 5 years?"'
    ]
  },
  'opposite-action': {
    name: 'Opposite Action',
    difficulty: 'Medium',
    time: '10-30 min',
    whatItIs: 'Opposite Action means acting against the urge of an emotion when that emotion doesn\'t fit the facts or isn\'t effective. If fear makes you want to avoid, you approach. If anger makes you want to attack, you speak gently. If shame makes you hide, you make eye contact.',
    whyItWorks: 'Emotions have action urges - they make you want to do something. When you repeatedly follow these urges, you strengthen the emotion. When you act opposite, you change the emotion itself. Your brain learns new associations and the emotion naturally decreases.',
    theScience: 'This works through extinction learning and behavioural activation. When you approach what you fear without bad consequences, your brain updates its threat database. When you engage when depressed, you activate the brain\'s reward system. Neurotransmitters shift: serotonin increases, cortisol decreases.',
    howToPractice: [
      'Identify the emotion and its action urge. Fear → avoid. Anger → attack. Sadness → withdraw. Shame → hide. Guilt → justify.',
      'Check if the emotion fits the facts (use Check the Facts). If it doesn\'t fit, use opposite action.',
      'Do the COMPLETE opposite, not halfway. If fear says hide in bed, get up, dress nicely, make plans, approach what scares you.',
      'Change your body language: If sad, stand tall and smile. If angry, relax your face and speak softly. If ashamed, make eye contact.',
      'Repeat until you feel the emotion shift. Once is rarely enough - commit to acting opposite consistently.',
      'Approach feared situations repeatedly until the fear decreases (exposure therapy).'
    ],
    tips: [
      'This will feel wrong at first - that\'s normal and okay',
      'You don\'t have to feel like doing it - just do it anyway',
      'Start with smaller opposite actions and build up',
      'The emotion will often intensify before it decreases - push through'
    ]
  },
  'please': {
    name: 'PLEASE',
    difficulty: 'Easy',
    time: 'Daily habit',
    whatItIs: 'PLEASE is a self-care acronym for building emotional resilience through physical health: treat Physical illness, balanced Eating, avoid mood-Altering substances, balanced Sleep, and Exercise. These fundamentals make your emotions more manageable.',
    whyItWorks: 'Your brain is part of your body. When your body is depleted, sick, or dysregulated, your emotion regulation systems can\'t work properly. It\'s much harder to manage anxiety when you\'ve had 4 hours of sleep or cope with depression when you haven\'t moved in days.',
    theScience: 'Physical health directly impacts neurotransmitter production. Exercise increases serotonin and endorphins. Sleep allows the brain to consolidate emotional memories and reset stress hormones. Nutrition provides building blocks for neurotransmitters. Substances disrupt normal brain chemistry, making emotions unpredictable.',
    howToPractice: [
      'Physical illness: Attend medical appointments, take prescribed medications, don\'t ignore symptoms. Untreated illness depletes emotional resources.',
      'Eating: Eat balanced meals regularly. Skipping meals causes blood sugar crashes that feel like anxiety or depression.',
      'Avoid mood-altering substances: Limit alcohol, cannabis, and other drugs. They provide short-term relief but worsen emotions overall.',
      'Sleep: Aim for consistent sleep/wake times and 7-9 hours. Sleep deprivation mimics mental illness symptoms.',
      'Exercise: Move your body most days, even for 10 minutes. Exercise is as effective as some antidepressants for mild-moderate depression.'
    ],
    tips: [
      'Don\'t try to change everything at once - pick one area to improve',
      'Track these in your daily check-in to spot patterns',
      'Notice how your emotions shift when you neglect PLEASE',
      'These aren\'t luxuries - they\'re medication'
    ]
  },
  'accumulate-positive': {
    name: 'Accumulate Positive Experiences',
    difficulty: 'Easy',
    time: 'Daily',
    whatItIs: 'This skill involves intentionally creating positive experiences and emotions in your life, both short-term (pleasant activities now) and long-term (building a life worth living). You actively increase positive emotions to balance negative ones.',
    whyItWorks: 'Depression and chronic stress shrink your world. You stop doing things you enjoy, which makes you feel worse, so you do even less. This skill reverses that cycle. Positive experiences don\'t just feel good - they build resilience, making it easier to handle challenges.',
    theScience: 'Positive experiences activate the brain\'s reward system (dopamine pathways), which depression dampens. Regular positive experiences can literally rewire depressive brain patterns. Neuroplasticity research shows that repeated positive experiences strengthen neural pathways for joy, enthusiasm, and hope.',
    howToPractice: [
      'Short-term: Do one pleasant thing every day. This can be small - favourite tea, a walk, calling a friend, watching sunset, petting an animal.',
      'Make a list of activities you enjoy or used to enjoy. Rate them by how positive they make you feel and how difficult they are.',
      'Schedule positive activities like appointments. Don\'t wait until you "feel like it" - motivation follows action.',
      'Long-term: Identify your values and take steps toward a meaningful life. What matters to you? Relationships? Creativity? Helping others?',
      'Build mastery: Do things that make you feel capable and accomplished, even if small. Learn something new. Complete a task. Create something.',
      'Practice mindfulness during positive experiences so you actually notice and absorb them. Savour the moment.'
    ],
    tips: [
      'When depressed, even small positive activities take effort - that\'s normal',
      'You might not feel the full positive emotion at first - keep practising',
      'Take photos or notes of positive moments to look back on',
      'Balance easy pleasures with meaningful activities'
    ]
  },

  // MINDFULNESS SKILLS
  'observe': {
    name: 'Observe',
    difficulty: 'Medium',
    time: '5-10 min',
    whatItIs: 'Observe means noticing your experience without putting words to it. You sense, watch, and notice sensations, thoughts, and emotions as they arise and pass, like watching clouds move across the sky.',
    whyItWorks: 'When you observe without labelling or judging, you create space between yourself and your experience. You realise you are not your thoughts or emotions - you\'re the awareness experiencing them. This reduces their power over you.',
    theScience: 'Observation activates the brain\'s default mode network differently than rumination does. It increases activity in the insula (interoceptive awareness) without triggering the rumination networks. Regular practice thickens the prefrontal cortex and increases grey matter in areas associated with emotional regulation.',
    howToPractice: [
      'Just notice: Sensations in your body, sounds around you, thoughts passing through your mind, emotions arising.',
      'Control your attention: When your mind wanders (it will), gently bring it back. Don\'t judge the wandering.',
      'Observe without words: Experience your breath without saying "in" and "out." Notice the colour red without saying "red."',
      'Notice that everything changes: Sensations come and go. Thoughts appear and disappear. Emotions rise and fall.',
      'Practice with all senses: Observe sights, sounds, smells, tastes, touch, and your internal body sensations.',
      'Step back: Imagine you\'re sitting on a riverbank watching your thoughts float by like leaves on the water.'
    ],
    tips: [
      'This is harder than it sounds - our minds love to label and judge',
      'You\'re not trying to stop thoughts - just notice them',
      'Start with external observations (sounds, sights) before internal ones',
      'Even 2 minutes of pure observation helps'
    ]
  },
  'describe': {
    name: 'Describe',
    difficulty: 'Easy',
    time: '5-10 min',
    whatItIs: 'Describe means putting words on your experience, but sticking to facts rather than interpretations. You label what you observe: "I notice my chest tightening," "I\'m having the thought that I\'ll fail," "I feel sadness."',
    whyItWorks: 'Naming emotions and sensations activates the prefrontal cortex and calms the amygdala. When you say "I\'m feeling anxious" rather than just feeling overwhelmed, you create psychological distance and remind yourself that feelings are temporary states, not unchangeable facts.',
    theScience: 'Research calls this "affect labelling" - simply naming emotions reduces their intensity. fMRI studies show that verbalizing emotions decreases amygdala activation. The act of labelling engages the reasoning brain, which inhibits the emotional brain. Even writing "I feel sad" activates regulation.',
    howToPractice: [
      'Stick to facts: What would a camera record? What can be sensed directly? Don\'t add interpretations, judgments, or assumptions.',
      'Label emotions: "I notice I\'m feeling angry." "I\'m experiencing sadness." "Anxiety is present."',
      'Describe thoughts: "I\'m having the thought that I\'m not good enough." This separates you from the thought.',
      'Describe sensations: "My heart is beating fast." "My jaw is clenched." "I feel tension in my shoulders."',
      'Notice the difference between "I am anxious" (merged with the emotion) and "I feel anxiety" (observing it).',
      'Practice describing without evaluating: "My heart is racing" not "My heart is racing which is terrible."'
    ],
    tips: [
      'Write descriptions in a journal - seeing them helps',
      'Use "I notice" or "I\'m having the thought" to create distance',
      'Describe out loud when alone - hearing yourself helps',
      'This skill pairs beautifully with Observe'
    ]
  },
  'participate': {
    name: 'Participate',
    difficulty: 'Easy',
    time: 'Ongoing',
    whatItIs: 'Participate means throwing yourself completely into activities, becoming one with what you\'re doing. You enter "flow" - that state where you\'re so absorbed you lose track of time and self-consciousness disappears.',
    whyItWorks: 'When you fully participate, you can\'t simultaneously ruminate or worry. Self-consciousness and anxiety require divided attention - part of you watching and judging. Full participation means no divided attention, no judgment, just experience.',
    theScience: 'Flow states increase dopamine and endorphins whilst decreasing cortisol. The brain\'s default mode network (responsible for self-referential thinking and rumination) actually quiets during flow. Time perception changes because the prefrontal cortex downregulates. This is why "flow" feels therapeutic.',
    howToPractice: [
      'Choose activities that match your skill level - not too easy (boring) or too hard (frustrating).',
      'Let go of self-consciousness. Stop watching yourself and judging your performance. Just do.',
      'Forget about learning or improving or looking good. Just experience the activity fully.',
      'Throw yourself in: Dance like no one\'s watching. Sing without evaluating your voice. Play without caring about winning.',
      'Notice when you\'re in your head and gently return to the activity. "I\'m not here to analyse, I\'m here to experience."',
      'Practice with movement: walking, dancing, exercise, cooking, gardening, art, playing with children or pets.'
    ],
    tips: [
      'This is easier with activities you already enjoy',
      'Start with short periods - 5 minutes of full participation is powerful',
      'Notice how different this feels from your usual self-conscious mode',
      'This is the opposite of overthinking - it\'s underthinking'
    ]
  },
  'wise-mind': {
    name: 'Wise Mind',
    difficulty: 'Hard',
    time: '10-15 min',
    whatItIs: 'Wise Mind is the integration of emotion mind (feelings, urges, mood-dependent thoughts) and reasonable mind (logic, facts, rationality). It\'s that place of intuitive knowing where both logic and emotion are honoured.',
    whyItWorks: 'Operating from pure emotion leads to impulsive, regrettable decisions. Operating from pure logic disconnects you from your values and intuition. Wise Mind balances both, leading to decisions that feel right and make sense.',
    theScience: 'Wise Mind represents integration between the limbic system (emotional brain) and prefrontal cortex (reasoning brain). Neurologically, it\'s associated with interoceptive awareness and the insula - the brain region that integrates emotional and physical information. This integration is what people describe as "gut feelings" or intuition.',
    howToPractice: [
      'Recognise Emotion Mind: Everything feels urgent. You think in extremes. You want to act immediately. Your judgment is mood-dependent.',
      'Recognise Reasonable Mind: You\'re disconnected from feelings. Everything is logic and facts. You might feel numb or robotic.',
      'Access Wise Mind: Breathe slowly and ask "What feels right deep down?" "What do I know to be true?" "What would I tell someone I love?"',
      'Focus on your breath: Wise Mind often appears in the pause after exhale. Ask your question and wait for the answer to arise.',
      'Notice body sensations: Wise Mind often communicates through body sense - what "feels right" in your chest or gut.',
      'Ask: "What would my wisest self do?" Not your anxious self, angry self, or people-pleasing self - your wisest self.',
      'Practice when calm so you can access it more easily in crisis.'
    ],
    tips: [
      'Wise Mind knows when emotion or reasonable mind should take the lead',
      'It might take time for the answer to emerge - be patient',
      'Wise Mind often feels quiet and certain, without emotional intensity',
      'This is not the voice of anxiety (urgent) or depression (hopeless)'
    ]
  },

  // INTERPERSONAL EFFECTIVENESS SKILLS
  'dear-man': {
    name: 'DEAR MAN',
    difficulty: 'Medium',
    time: '10-15 min',
    whatItIs: 'DEAR MAN is a structured approach to assertive communication when you want to achieve a goal or make a request. It stands for: Describe, Express, Assert, Reinforce, stay Mindful, Appear confident, Negotiate.',
    whyItWorks: 'Most of us either avoid asking for what we need (and feel resentful) or ask in aggressive ways (and damage relationships). DEAR MAN gives you a middle path - clear, respectful, effective communication that gets results whilst maintaining relationships.',
    theScience: 'Assertive communication activates the ventrolateral prefrontal cortex (emotional regulation) and reduces amygdala activity (threat response). When you communicate assertively, your body produces less cortisol (stress hormone). Research shows assertiveness training improves both self-esteem and relationship satisfaction.',
    howToPractice: [
      'Describe: State the facts without judgment. "When you cancelled our plans at the last minute..." not "You\'re so inconsiderate!"',
      'Express: Share your feelings or thoughts using "I" statements. "I felt disappointed and hurt."',
      'Assert: Ask clearly for what you want. "I\'d like you to give me 24 hours\' notice if you need to cancel."',
      'Reinforce: Explain positive effects of getting what you want. "That way, I can make other plans and I won\'t feel let down."',
      'stay Mindful: Keep focused on your goal. Don\'t get distracted by attacks or irrelevant points. Broken record: repeat your request.',
      'Appear confident: Eye contact, steady voice, shoulders back. Confidence in delivery increases likelihood of success.',
      'Negotiate: Be willing to compromise. "If 24 hours isn\'t possible, could you give me as much notice as you can?"'
    ],
    tips: [
      'Write out a DEAR MAN script before difficult conversations',
      'Practice with small requests before big ones',
      'You have a right to ask - they have a right to say no',
      'Stay calm even if they get defensive'
    ]
  },
  'give': {
    name: 'GIVE',
    difficulty: 'Easy',
    time: 'In conversation',
    whatItIs: 'GIVE is a skill for maintaining relationships whilst communicating. It helps you be assertive without damaging connections. It stands for: be Gentle, act Interested, Validate, use an Easy manner.',
    whyItWorks: 'You can be right and still damage relationships through harsh delivery. GIVE ensures your message is heard because the other person doesn\'t get defensive. When people feel respected and understood, they\'re more likely to listen and cooperate.',
    theScience: 'Validation activates the ventral striatum (reward centre) in the listener\'s brain. Gentle communication reduces the other person\'s stress response. Mirror neurons mean that your calm, interested manner triggers similar states in others. The social engagement system responds positively to validation.',
    howToPractice: [
      'be Gentle: No threats, attacks, or harsh words. Don\'t show disrespect, judgment, or contempt. Stay calm.',
      'act Interested: Listen actively. Show you care about their perspective. Avoid interrupting. Ask questions.',
      'Validate: Acknowledge their feelings and perspective, even if you disagree with facts. "I can see why you\'d feel that way."',
      'use an Easy manner: Smile when appropriate. Use humour lightly. Don\'t be overly intense or serious. Be diplomatic.',
      'Remember: You can validate feelings whilst still disagreeing with actions or requesting change.',
      'Avoid sarcasm, eye-rolling, or dismissive body language.'
    ],
    tips: [
      'GIVE is especially important in close relationships',
      'Use with DEAR MAN for balanced assertiveness',
      'You\'re not being fake - you\'re being strategic',
      'People remember how you made them feel'
    ]
  },
  'fast': {
    name: 'FAST',
    difficulty: 'Medium',
    time: 'In situation',
    whatItIs: 'FAST is a skill for maintaining self-respect in interactions. It ensures you don\'t sacrifice your values or integrity to keep the peace or get what you want. It stands for: be Fair, no Apologies, Stick to values, be Truthful.',
    whyItWorks: 'When you compromise your values or dignity to please others, you may win in the moment but lose respect for yourself. This creates shame, resentment, and decreased self-worth. FAST helps you maintain integrity even in difficult situations.',
    theScience: 'Acting in line with personal values activates the ventromedial prefrontal cortex, associated with meaning and reward. Compromising values creates cognitive dissonance, which the brain experiences as distressing. Studies show that values-consistent behaviour increases self-esteem and reduces depression.',
    howToPractice: [
      'be Fair: To yourself AND others. Don\'t be a doormat, but don\'t be unfairly demanding either.',
      'no Apologies: Don\'t over-apologise for existing, having feelings, or making requests. Only apologise for actual wrongs.',
      'Stick to values: Know what matters to you (honesty, kindness, responsibility) and don\'t compromise these to please others.',
      'be Truthful: Don\'t lie, exaggerate, or make excuses. Be honest but tactful. Your truth matters.',
      'Notice when you\'re sacrificing yourself: Are you lying to avoid conflict? Apologizing for things that aren\'t your fault?',
      'Practice saying what you mean: Stop hedging with "maybe," "I don\'t know," "sorry, but..."'
    ],
    tips: [
      'FAST prevents resentment that builds when you betray yourself',
      'Balance FAST with GIVE - be true to yourself AND kind to others',
      'Over-apologizing diminishes your message',
      'Your self-respect is as important as the other person\'s'
    ]
  },
  'boundaries': {
    name: 'Boundary Setting',
    difficulty: 'Hard',
    time: 'Ongoing',
    whatItIs: 'Boundaries are limits you set about how others can treat you and what you\'re willing to do. They protect your time, energy, emotions, and values. Healthy boundaries are clear, consistent, and enforced.',
    whyItWorks: 'Without boundaries, you\'ll exhaust yourself trying to meet everyone else\'s needs whilst neglecting your own. Boundaries prevent resentment, burnout, and relationship dysfunction. They teach others how to treat you and create space for mutual respect.',
    theScience: 'Boundary-setting activates the prefrontal cortex and requires emotional regulation. Porous boundaries keep you in chronic stress states (high cortisol). Clear boundaries reduce stress hormones and increase feelings of safety and autonomy. Research links strong boundaries to better mental health outcomes.',
    howToPractice: [
      'Identify where you need boundaries: Notice where you feel resentful, exhausted, taken advantage of, or violated.',
      'Get clear on your limits: What are you willing/unwilling to do? What feels okay vs. not okay?',
      'Communicate clearly: "I\'m not available to talk after 9pm." "I don\'t lend money to friends." "I need advance notice for visits."',
      'Enforce consequences: If someone repeatedly violates boundaries, follow through. "If you call after 9pm, I won\'t answer."',
      'Tolerate guilt: Boundary-setting often triggers guilt, especially if you\'re used to people-pleasing. This is normal.',
      'Start small: Set a boundary with someone safe before tackling difficult relationships.',
      'Remember: "No" is a complete sentence. You don\'t owe lengthy explanations.'
    ],
    tips: [
      'Healthy relationships respect boundaries',
      'People who get angry at your boundaries are revealing themselves',
      'You teach people how to treat you through what you allow',
      'Boundaries are self-care, not selfishness'
    ]
  },

  // COGNITIVE SKILLS
  'thought-records': {
    name: 'Thought Records',
    difficulty: 'Medium',
    time: '10-15 min',
    whatItIs: 'Thought records are structured tools for examining and challenging negative automatic thoughts. You identify the situation, your thought, the resulting emotion, evidence for/against the thought, and develop a balanced alternative perspective.',
    whyItWorks: 'Most negative thoughts happen automatically and go unchallenged. We accept them as truth. Thought records slow down this process and engage your rational brain to evaluate whether thoughts are accurate or helpful. They break the cycle of negative thinking.',
    theScience: 'This is core Cognitive Behavioral Therapy (CBT). Thought records engage the prefrontal cortex to regulate amygdala-driven emotional responses. Research shows CBT (which uses thought records) is as effective as medication for depression and anxiety. It literally rewires neural pathways through cognitive reappraisal.',
    howToPractice: [
      'Situation: Describe what happened (just facts). "My friend didn\'t reply to my text."',
      'Automatic thought: What went through your mind? "She\'s angry at me. I did something wrong."',
      'Emotion: What did you feel and how intense (0-10)? "Anxious (8), Sad (6)"',
      'Evidence for: What supports this thought? "She usually replies quickly."',
      'Evidence against: What doesn\'t support it? "She might be busy. She\'s not replied late before and wasn\'t angry. I haven\'t done anything wrong that I know of."',
      'Alternative thought: More balanced view? "She\'s probably busy. If something\'s wrong, she\'ll tell me."',
      'Re-rate emotion: How do you feel now (0-10)? "Anxious (4), Sad (2)"'
    ],
    tips: [
      'Write it down - doing it in your head isn\'t as effective',
      'Do this regularly, especially when emotions are intense',
      'Look for cognitive distortions (catastrophizing, mind-reading, etc.)',
      'The goal isn\'t positive thinking - it\'s accurate thinking'
    ]
  },
  'cognitive-distortions': {
    name: 'Identify Cognitive Distortions',
    difficulty: 'Easy',
    time: '5-10 min',
    whatItIs: 'Cognitive distortions are predictable errors in thinking that make situations seem worse than they are. Common ones include all-or-nothing thinking, catastrophizing, mind-reading, and should statements. Learning to spot them is the first step to changing them.',
    whyItWorks: 'Once you recognise your distortion patterns, they lose power. You realise "Oh, that\'s just my brain catastrophising again" instead of accepting the thought as fact. Naming the distortion creates distance and activates your rational brain.',
    theScience: 'Different distortions activate different neural pathways. Catastrophizing activates threat detection systems. All-or-nothing thinking reflects rigid, black-and-white processing. Learning to identify these patterns increases metacognitive awareness - your ability to think about your thinking - which is associated with better emotional regulation.',
    howToPractice: [
      'All-or-nothing: Seeing things in extremes with no middle ground. "If I\'m not perfect, I\'m a failure."',
      'Catastrophizing: Jumping to worst-case scenarios. "If I mess up this presentation, I\'ll lose my job and become homeless."',
      'Mind-reading: Assuming you know what others think. "She thinks I\'m stupid."',
      'Fortune-telling: Predicting negative outcomes. "I know this will go badly."',
      'Emotional reasoning: Believing feelings are facts. "I feel worthless, so I must be worthless."',
      'Should statements: Rigid rules about how things "should" be. "I should never make mistakes."',
      'Labelling: Attaching negative labels. "I\'m a loser" instead of "I made a mistake."',
      'Personalisation: Taking everything personally. "She\'s in a bad mood because of me."',
      'Mental filter: Focusing only on negatives. "The whole evening was ruined" (because of one awkward moment).',
      'Disqualifying the positive: Rejecting positive experiences. "That compliment doesn\'t count."'
    ],
    tips: [
      'Most people have 2-3 favourite distortions they use repeatedly',
      'Keep a list of distortions on your phone to reference',
      'Once you spot the distortion, ask: "What would be more accurate?"',
      'This skill becomes automatic with practice'
    ]
  },
  'cognitive-reframing': {
    name: 'Cognitive Reframing',
    difficulty: 'Medium',
    time: '10-15 min',
    whatItIs: 'Cognitive reframing means looking at a situation from a different perspective - finding an alternative way to interpret events that\'s more balanced, helpful, or empowering. It\'s not about positive thinking, but about flexible thinking.',
    whyItWorks: 'The meaning you assign to events determines your emotional response, not the events themselves. Two people can experience the same situation and feel completely differently based on their interpretation. Reframing gives you power over your emotional experience.',
    theScience: 'Reappraisal (reframing) is one of the most effective emotion regulation strategies. fMRI studies show it decreases amygdala activity and increases prefrontal cortex activity. Regular practice literally changes brain structure, strengthening neural pathways for flexible, adaptive thinking.',
    howToPractice: [
      'Identify your initial interpretation: "This is terrible. I\'m a failure."',
      'Ask reframing questions: "What else could this mean? What would I tell a friend? What might I learn from this? What\'s the opportunity here?"',
      'Consider context: "What factors beyond my control contributed? What was I dealing with at the time?"',
      'Zoom out: "Will this matter in 5 years? How might I see this differently looking back?"',
      'Find growth: "What can I learn? How might this make me stronger? What skill can I develop?"',
      'Practice alternative views: "Instead of \'I failed,\' could this be \'I tried something difficult and learned what doesn\'t work\'?"',
      'Balance: Not everything needs to be reframed positively - sometimes acknowledgment of difficulty is what\'s needed first.'
    ],
    tips: [
      'Reframing isn\'t denying or minimising pain',
      'Start with smaller frustrations before big traumas',
      'Write down multiple reframes to find ones that feel authentic',
      'Combine with thought records for powerful effect'
    ]
  },
  'core-beliefs': {
    name: 'Core Belief Work',
    difficulty: 'Hard',
    time: 'Ongoing',
    whatItIs: 'Core beliefs are deep, fundamental beliefs about yourself, others, and the world, usually formed in childhood. They operate automatically and filter all your experiences. Common negative core beliefs include "I\'m unlovable," "I\'m defective," "The world is dangerous."',
    whyItWorks: 'Core beliefs generate automatic thoughts. If you only challenge surface thoughts without addressing underlying beliefs, you\'re bailing water from a boat without fixing the leak. Changing core beliefs creates lasting change in how you think, feel, and behave.',
    theScience: 'Core beliefs are encoded in implicit memory networks formed early in life. They become neural superhighways that information automatically travels down. Schema therapy (core belief work) can reorganize these networks. Neuroplasticity research shows that with consistent work, even deeply ingrained neural patterns can change.',
    howToPractice: [
      'Identify your core beliefs: Notice patterns. What do you believe about yourself deep down? What themes repeat in your negative thoughts?',
      'Track evidence against the belief: Keep a log of experiences that contradict it. "I believe I\'m unlovable, but my friend called to check on me."',
      'Explore origins: When did you first learn this belief? Was it accurate then? Is it accurate now?',
      'Develop alternative core beliefs: What would be more accurate and helpful? "I\'m worthy of love even with flaws."',
      'Act as if: Behave as if the new belief is true. How would someone who believes "I\'m capable" act?',
      'Be patient: Core beliefs took years to form. They won\'t change instantly. Consistent practice over time is key.',
      'Consider therapy: Core belief work often benefits from professional support, especially for trauma-based beliefs.'
    ],
    tips: [
      'Core belief work is deep and can be painful - go gently',
      'You may not fully believe alternative beliefs at first - that\'s normal',
      'Look for black-and-white beliefs to challenge - reality is nuanced',
      'This work compounds - small shifts create big changes over time'
    ]
  },

  // GROUNDING TECHNIQUES
  '54321': {
    name: '5-4-3-2-1 Senses',
    difficulty: 'Easy',
    time: '2-5 min',
    whatItIs: 'The 5-4-3-2-1 technique grounds you in the present moment by engaging all five senses. You identify 5 things you can see, 4 you can touch, 3 you can hear, 2 you can smell, and 1 you can taste.',
    whyItWorks: 'Anxiety, panic, and dissociation pull you out of the present - into feared futures or traumatic pasts. Grounding through senses forces your attention back to right now, where you\'re safe. It interrupts panic spirals and brings you back into your body.',
    theScience: 'Sensory grounding activates the thalamus and sensory cortices, redirecting neural activity away from the amygdala (fear centre) and rumination networks. It engages the parasympathetic nervous system, triggering the relaxation response. Bottom-up regulation (through senses) works faster than top-down (through thoughts).',
    howToPractice: [
      '5 things you can see: Look around. Name them out loud or silently. Really notice details - colors, shapes, textures.',
      '4 things you can touch/feel: Notice physical sensations. The chair supporting you. Your feet on the floor. Your clothes on your skin. Touch something nearby.',
      '3 things you can hear: Notice sounds. Traffic. Birds. Your breath. The hum of electronics. Distant voices.',
      '2 things you can smell: This is harder. Notice any scents. Your clothes. The air. If nothing, smell something (hand lotion, coffee).',
      '1 thing you can taste: Notice any taste in your mouth. Take a sip of water. Pop a mint. Lick your lips.',
      'Slow down and really engage with each sense. This isn\'t a race - it\'s an anchoring exercise.'
    ],
    tips: [
      'Say items out loud if possible - hearing yourself grounds you more',
      'Don\'t just list items - really notice them with full attention',
      'You can modify the numbers (3-3-3 or 5-5-5) if needed',
      'This works for panic, dissociation, flashbacks, and overwhelm'
    ]
  },
  'body-scan': {
    name: 'Body Scan',
    difficulty: 'Easy',
    time: '5-15 min',
    whatItIs: 'A body scan involves slowly moving your attention through your body, noticing sensations in each area without trying to change them. You scan from toes to head (or reverse), simply observing what you feel.',
    whyItWorks: 'Trauma, anxiety, and dissociation disconnect you from your body. You live in your head, unaware of physical signals. Body scans rebuild this connection, helping you recognise emotions in your body before they become overwhelming. They also release tension you didn\'t know you were holding.',
    theScience: 'Body scans increase interoceptive awareness - your ability to sense internal states. This activates the insula and enhances connection between body and brain. Regular practice increases grey matter in areas associated with self-awareness and emotional regulation. It also activates the parasympathetic nervous system.',
    howToPractice: [
      'Find a comfortable position - sitting or lying down.',
      'Start with your feet: Notice any sensations - warmth, coolness, tingling, pressure, tension, or numbness.',
      'Move slowly up your body: Ankles, calves, knees, thighs, hips, stomach, chest, back, shoulders, arms, hands, neck, face, head.',
      'Don\'t try to change anything: Just notice. If you find tension, acknowledge it without forcing relaxation.',
      'When your mind wanders (it will), gently return attention to the body part you\'re scanning.',
      'Breathe naturally: Some people coordinate the scan with breath, moving attention on each exhale.',
      'End by noticing your body as a whole for a few breaths.'
    ],
    tips: [
      'Guided body scan recordings can help at first',
      'This gets easier with practice - don\'t judge early attempts',
      'Great for insomnia - do it in bed to aid sleep',
      'You might notice emotions arising - that\'s normal and okay'
    ]
  },
  'safe-place': {
    name: 'Safe Place Visualisation',
    difficulty: 'Medium',
    time: '5-10 min',
    whatItIs: 'Safe place visualisation involves creating a detailed mental image of a place where you feel completely safe, calm, and at peace. It can be real or imaginary. You engage all senses to make it vivid.',
    whyItWorks: 'Your brain doesn\'t fully distinguish between imagined and real experiences. When you vividly imagine your safe place, your body responds as if you\'re actually there - heart rate slows, muscles relax, stress hormones decrease. It\'s a mental refuge you can access anytime.',
    theScience: 'Visualisation activates the same neural networks as actual experience. When you imagine a peaceful beach, your visual cortex, auditory cortex, and somatosensory cortex all activate. This triggers the relaxation response. Mental imagery is a powerful tool for emotional regulation.',
    howToPractice: [
      'Choose your safe place: Beach, forest, childhood room, imaginary sanctuary - anywhere you feel safe and calm.',
      'Close your eyes and imagine yourself there. What do you see? Notice colors, light, details.',
      'What do you hear? Waves, birds, wind, silence, music?',
      'What do you smell? Salt air, pine trees, freshly baked bread?',
      'What do you feel on your skin? Sun warmth, cool breeze, soft blanket?',
      'What emotions arise? Safety, peace, contentment? Let yourself feel them.',
      'Spend several minutes here, exploring with all senses.',
      'Practice when calm so you can access it more easily during distress.',
      'Some people create a cue - a word or gesture - to quickly access the safe place.'
    ],
    tips: [
      'The more detailed and sensory, the more powerful',
      'It\'s okay if your mind wanders - gently return to the place',
      'You can have multiple safe places for different needs',
      'This is YOUR place - no one can enter unless you invite them'
    ]
  },
  'grounding-objects': {
    name: 'Grounding Objects',
    difficulty: 'Easy',
    time: '1-3 min',
    whatItIs: 'Grounding objects are physical items you carry or keep nearby to anchor you in the present during distress. You focus all your attention on the object\'s properties - texture, weight, temperature, appearance.',
    whyItWorks: 'Physical objects provide immediate sensory input that pulls you out of internal distress and into the external present moment. The tactile focus interrupts panic, dissociation, or rumination. Having a designated object creates a reliable ritual for returning to calm.',
    theScience: 'Touch activates mechanoreceptors in your skin, sending signals directly to the somatosensory cortex. This bottom-up sensory input can override top-down emotional processing. Focused attention on texture engages the prefrontal cortex, interrupting amygdala activation. Bilateral stimulation (holding objects in both hands) can enhance grounding.',
    howToPractice: [
      'Choose your object: Smooth stone, soft fabric, piece of jewelry, textured ball, ice cube, stress ball.',
      'When distressed, hold the object and focus all attention on it.',
      'Notice: Temperature (warm or cool?), Weight (heavy or light?), Texture (smooth, rough, soft, hard?), Shape, Color.',
      'Describe it in detail: "This stone is cool and smooth. It fits perfectly in my palm. It has grey and white swirls. It weighs about..."',
      'Trace your fingers over it slowly, noticing every detail.',
      'Keep grounding objects in your pocket, bag, car, bedside table.',
      'You can have different objects for different situations.'
    ],
    tips: [
      'Choose objects with strong sensory properties',
      'Ice cubes are powerful for intense distress - the cold activates strongly',
      'Combine with breathing - hold the object and breathe slowly',
      'This works brilliantly for dissociation and panic attacks'
    ]
  }
};

export const SkillDetailDialog = ({ skillId, onClose }: SkillDetailDialogProps) => {
  if (!skillId || !skillsContent[skillId]) return null;

  const skill = skillsContent[skillId];

  return (
    <Dialog open={!!skillId} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1">
              <DialogTitle className="text-2xl mb-2">{skill.name}</DialogTitle>
              <div className="flex items-center gap-3">
                <Badge variant="secondary">{skill.difficulty}</Badge>
                <span className="text-sm text-muted-foreground">⏱️ {skill.time}</span>
              </div>
            </div>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="h-5 w-5" />
            </Button>
          </div>
          <DialogDescription className="text-lg leading-relaxed">
            {skill.whatItIs}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 mt-6">
          {/* Why It Works */}
          <Card className="bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20">
            <CardHeader>
              <div className="flex items-center gap-3 text-primary mb-2">
                <Heart className="h-6 w-6" />
                <h3 className="font-medium text-xl">Why It Works</h3>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-foreground leading-relaxed text-base">{skill.whyItWorks}</p>
            </CardContent>
          </Card>

          {/* The Science */}
          <Card className="bg-gradient-to-br from-accent/5 to-accent/10 border-accent/20">
            <CardHeader>
              <div className="flex items-center gap-3 text-primary mb-2">
                <Brain className="h-6 w-6" />
                <h3 className="font-medium text-xl">The Science</h3>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-foreground leading-relaxed text-base">{skill.theScience}</p>
            </CardContent>
          </Card>

          {/* How to Practice */}
          <Card className="bg-gradient-to-br from-secondary/5 to-secondary/10 border-secondary/20">
            <CardHeader>
              <div className="flex items-center gap-3 text-secondary mb-2">
                <CheckCircle2 className="h-6 w-6" />
                <h3 className="font-medium text-xl">How to Practise</h3>
              </div>
            </CardHeader>
            <CardContent>
              <ol className="space-y-4">
                {skill.howToPractice.map((step, index) => (
                  <li key={index} className="flex gap-3">
                    <span className="font-medium text-secondary flex-shrink-0 text-base">{index + 1}.</span>
                    <span className="text-foreground leading-relaxed text-base">{step}</span>
                  </li>
                ))}
              </ol>
            </CardContent>
          </Card>

          {/* Tips */}
          {skill.tips && skill.tips.length > 0 && (
            <Card className="bg-gradient-to-br from-background to-primary/5 border-primary/20">
              <CardHeader>
                <div className="flex items-center gap-3 text-primary mb-2">
                  <Lightbulb className="h-6 w-6" />
                  <h3 className="font-medium text-xl">Helpful Tips</h3>
                </div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  {skill.tips.map((tip, index) => (
                    <li key={index} className="flex gap-3 items-start">
                      <span className="text-primary mt-1 text-lg">•</span>
                      <span className="text-foreground leading-relaxed text-base">{tip}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          <Separator />

          {/* Action Buttons */}
          <div className="flex gap-3 justify-end">
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
            <Button className="bg-primary text-primary-foreground hover:bg-primary/90">
              Mark as Practised
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
